import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { DocumentPreview, TreeNode, Annotation } from '../types';
import { X, Download, Plus, Minus, FileText, Cloud, ArrowLeft, ArrowRight, MessageSquare, FileSpreadsheet } from 'lucide-react';
import { SimplePDFViewer, SimplePDFViewerRef } from './SimplePDFViewer';
import { AnnotationContext } from './PdfOutlinePanel';
import { buildDocKey } from '../services/annotations';
import { useAuth } from '../contexts/AuthContext';
import { isSharePointFile, loadSharePointFileContent } from '../services/lazy-file-loader';
import './DocumentViewer.css';

interface DocumentViewerProps {
  document: DocumentPreview | null;
  onClose: () => void;
  onDocumentSelect?: (document: DocumentPreview) => void;
  tree?: TreeNode[];
  allSequences?: Array<{ id: string; tree: TreeNode[] }>;
  cumulativeTree?: TreeNode[]; // Merged tree from cumulative view (lazy-loaded)
  getCumulativeTree?: () => TreeNode[]; // Function to build cumulative tree on-demand
  appId?: string; // Application folder id - part of the shared annotation identity
  annotations?: Annotation[]; // All review comments in the application (live)
  currentSequence?: string | null; // Currently selected sequence (for the count badge)
  onExportComments?: () => void; // Export all comments to Excel (shared with toolbar)
}

interface NavigationHistoryItem {
  document: DocumentPreview;
  timestamp: number;
  pageNumber?: number; // Store page number for PDF documents
}

// Helper function to normalize a name for comparison (lowercase, remove special chars)
function normalizeName(name: string): string {
  return name.toLowerCase().trim();
}

// Helper function to get the actual file path from a file node (without app folder and sequence folder)
function getFileSystemPath(node: TreeNode): string | null {
  if (node.type !== 'file') return null;
  
  // Try to get the actual file system path
  const file = node.file;
  if (file && (file as any).webkitRelativePath) {
    const fullPath = (file as any).webkitRelativePath.toLowerCase().replace(/\\/g, '/');
    
    // Extract path without app folder and sequence folder
    // webkitRelativePath format: "app-folder/sequence-id/module/..."
    // Link path format: "module/..."
    const parts = fullPath.split('/');
    if (parts.length >= 3) {
      // Skip app folder (index 0) and sequence folder (index 1), keep the rest
      return parts.slice(2).join('/');
    }
    
    return fullPath;
  }
  
  return null;
}

// Find a file node by exact webkitRelativePath match (case-insensitive, slash-normalized).
// Used for same-directory link resolution to bypass fuzzy matching when colliding basenames
// exist in sibling sub-trees (flat-published sequences).
function findNodeByWebkitPath(nodes: TreeNode[], targetWebkitPath: string): TreeNode | null {
  const target = targetWebkitPath.toLowerCase().replace(/\\/g, '/');
  for (const node of nodes) {
    if (node.type === 'file') {
      const path = (node.file as { webkitRelativePath?: string } | undefined)?.webkitRelativePath;
      if (path && path.toLowerCase().replace(/\\/g, '/') === target) {
        return node;
      }
    } else if (node.type === 'folder' && node.children) {
      const found = findNodeByWebkitPath(node.children, targetWebkitPath);
      if (found) return found;
    }
  }
  return null;
}

// Helper function to find a document in a tree by matching file system paths
// Prioritizes exact path matches over name-only matches
function findDocumentInTreeByPath(nodes: TreeNode[], linkPath: string, currentPath: string[] = []): TreeNode | null {
  // Normalize the link path
  const normalizedLinkPath = linkPath.toLowerCase().replace(/\\/g, '/').replace(/^\/+/, '');
  const linkPathParts = normalizedLinkPath.split('/').filter(Boolean);
  const linkFileName = linkPathParts[linkPathParts.length - 1] || '';
  
  // If link path has multiple parts, try to match the full path structure
  const isFullPath = linkPathParts.length > 1;
  
  // Recursively search all nodes
  for (const node of nodes) {
    if (node.type === 'file') {
      // Get the actual file system path for this file
      const filePath = getFileSystemPath(node);
      
      if (filePath) {
        const filePathParts = filePath.split('/').filter(Boolean);
        const nodeFileName = filePathParts[filePathParts.length - 1] || '';
        
        // PRIORITY 1: Exact path match (respects full path structure)
        if (isFullPath && filePath === normalizedLinkPath) {
          return node;
        }
        
        // PRIORITY 2: Path ends with link path (for relative paths)
        if (filePath.endsWith(normalizedLinkPath)) {
          return node;
        }
        
        // PRIORITY 3: Match path segments from the end (respects folder structure)
        // This handles cases where the resolved path might have extra intermediate folders
        if (isFullPath && linkPathParts.length >= 2) {
          // Try matching the last N segments (where N = linkPathParts.length)
          // This allows finding files even if there are extra folders in the middle
          if (filePathParts.length >= linkPathParts.length) {
            const filePathEnd = filePathParts.slice(-linkPathParts.length).join('/');
            if (filePathEnd === normalizedLinkPath) {
              return node;
            }
          }
          
          // Also try matching progressively fewer segments from the end
          // Start with more segments for better accuracy
          for (let segmentsToMatch = Math.min(linkPathParts.length, filePathParts.length); segmentsToMatch >= 2; segmentsToMatch--) {
            const linkEnd = linkPathParts.slice(-segmentsToMatch).join('/');
            const fileEnd = filePathParts.slice(-segmentsToMatch).join('/');
            if (linkEnd === fileEnd) {
              // Found a match - verify the filename matches too
              if (linkPathParts[linkPathParts.length - 1] === filePathParts[filePathParts.length - 1]) {
                return node;
              }
            }
          }
          
          // Check if last 2 segments match (filename + parent folder)
          if (linkPathParts.length >= 2 && filePathParts.length >= 2) {
            const linkLastTwo = linkPathParts.slice(-2).join('/');
            const fileLastTwo = filePathParts.slice(-2).join('/');
            if (linkLastTwo === fileLastTwo) {
              return node;
            }
          }
        }
        
        // PRIORITY 4: Filename match only (fallback - less reliable)
        if (linkFileName && nodeFileName && (
          linkFileName === nodeFileName ||
          linkFileName.replace(/\.pdf$/i, '') === nodeFileName.replace(/\.pdf$/i, '')
        )) {
          // Only return if it's NOT a full path (to avoid false matches)
          if (!isFullPath) {
            return node;
          }
          // For full paths, continue searching for better match
        }
      }
    } else if (node.type === 'folder' && node.children) {
      // Recursively search in children, tracking path
      const folderPath = [...currentPath, node.name];
      const found = findDocumentInTreeByPath(node.children, linkPath, folderPath);
      if (found) return found;
    }
  }
  
  // If no exact path match found, try matching by XML title or document name
  // This handles cases where links reference documents by their XML titles (e.g., "3.2.P.5.1 specification")
  const normalizedLinkPathForTitle = normalizeName(linkPath);
  
  for (const node of nodes) {
    if (node.type === 'file') {
      // PRIORITY: Check XML title (handles cases like "3.2.P.5.1 specification")
      if (node.xmlTitle) {
        const xmlTitleNormalized = normalizeName(node.xmlTitle);
        
        // Exact match on XML title
        if (xmlTitleNormalized === normalizedLinkPathForTitle) {
          return node;
        }
        
        // Partial match - check if link path is contained in XML title or vice versa
        // But be careful - "3.2.P.5.1" shouldn't match "3.2.P.4.1"
        // Only match if the link path is a complete segment or exact match
        if (normalizedLinkPathForTitle.length > 5) { // Only for meaningful matches
          // Check if XML title starts with or contains the link path as a complete segment
          const linkSegments = normalizedLinkPathForTitle.split(/\s+/);
          const xmlSegments = xmlTitleNormalized.split(/\s+/);
          
          // Try to match the first segment (e.g., "3.2.P.5.1" from "3.2.P.5.1 specification")
          if (linkSegments.length > 0 && xmlSegments.length > 0) {
            const linkFirstSegment = linkSegments[0];
            const xmlFirstSegment = xmlSegments[0];
            
            // Exact match on first segment (e.g., "3.2.P.5.1")
            if (linkFirstSegment === xmlFirstSegment && linkFirstSegment.length > 3) {
              return node;
            }
          }
          
          // Also check if XML title contains the full link path
          if (xmlTitleNormalized.includes(normalizedLinkPathForTitle) && 
              normalizedLinkPathForTitle.length > 5) {
            return node;
          }
        }
      }
      
      // Fallback: Check by filename if it's just a filename link
      if (!isFullPath && linkFileName) {
        const filePath = getFileSystemPath(node);
        if (filePath) {
          const nodeFileName = filePath.split('/').pop() || '';
          if (linkFileName === nodeFileName || 
              linkFileName.replace(/\.pdf$/i, '') === nodeFileName.replace(/\.pdf$/i, '')) {
            return node;
          }
        }
        
        // Also check by originalName/name
        const nodeName = normalizeName(node.originalName || node.name);
        if (nodeName === normalizeName(linkFileName) ||
            nodeName.replace(/\.pdf$/i, '') === normalizeName(linkFileName).replace(/\.pdf$/i, '')) {
          return node;
        }
      }
    } else if (node.type === 'folder' && node.children) {
      const found = findDocumentInTreeByPath(node.children, linkPath, currentPath);
      if (found) return found;
    }
  }
  
  return null;
}


// Helper function to find a document across all sequences by path
// Optimized to search efficiently without loading everything into memory
function findDocumentByPath(
  allSequences: Array<{ id: string; tree: TreeNode[] }> | undefined,
  currentTree: TreeNode[] | undefined,
  linkPath: string,
  cumulativeTree?: TreeNode[],
  getCumulativeTree?: () => TreeNode[]
): TreeNode | null {
  // First try current tree (fastest - already loaded)
  if (currentTree && currentTree.length > 0) {
    const found = findDocumentInTreeByPath(currentTree, linkPath);
    if (found) return found;
  }
  
  // Then try cumulative tree if already built (for cumulative view)
  if (cumulativeTree && cumulativeTree.length > 0) {
    const found = findDocumentInTreeByPath(cumulativeTree, linkPath);
    if (found) return found;
  }
  
  // Try building cumulative tree on-demand if function provided (lazy loading)
  if (getCumulativeTree) {
    try {
      const lazyCumulativeTree = getCumulativeTree();
      if (lazyCumulativeTree && lazyCumulativeTree.length > 0) {
        const found = findDocumentInTreeByPath(lazyCumulativeTree, linkPath);
        if (found) return found;
      }
    } catch (e) {
      console.warn('Failed to build cumulative tree on-demand:', e);
    }
  }
  
  // Last resort: search sequences individually (lazy - only searches until found)
  // This avoids building the full cumulative tree if not needed
  if (allSequences && allSequences.length > 0) {
    // Extract module from path to narrow search (e.g., "m5/..." -> search sequences with m5)
    const pathParts = linkPath.toLowerCase().split('/');
    const moduleMatch = pathParts.find(part => /^m[1-5]$/.test(part));
    
    // If we can identify a module, prioritize sequences that have that module
    // Use a lightweight check instead of JSON.stringify (which is expensive)
    const sequencesToSearch = moduleMatch 
      ? [...allSequences].sort((a, b) => {
          // Lightweight check: look for module in top-level folders
          const aHasModule = (a.tree || []).some((node: TreeNode) => 
            node.type === 'folder' && node.name.toLowerCase() === moduleMatch
          );
          const bHasModule = (b.tree || []).some((node: TreeNode) => 
            node.type === 'folder' && node.name.toLowerCase() === moduleMatch
          );
          if (aHasModule && !bHasModule) return -1;
          if (!aHasModule && bHasModule) return 1;
          return 0;
        })
      : allSequences;
    
    for (const sequence of sequencesToSearch) {
      const found = findDocumentInTreeByPath(sequence.tree, linkPath);
      if (found) return found; // Stop as soon as we find it
    }
  }
  
  return null;
}

export function DocumentViewer({ document: selectedDocument, onClose, onDocumentSelect, tree = [], allSequences, cumulativeTree, getCumulativeTree, appId, annotations = [], currentSequence, onExportComments }: DocumentViewerProps) {
  const { user } = useAuth();
  const viewerRef = useRef<HTMLDivElement>(null);
  const [showCommentsPanel, setShowCommentsPanel] = useState(false);
  const pdfViewerRef = useRef<SimplePDFViewerRef | null>(null);
  const [isFullyExpanded, setIsFullyExpanded] = useState(true);
  const [loadingContent, setLoadingContent] = useState(false);
  const [actualFile, setActualFile] = useState<File | null>(null);
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);
  const [navigationHistory, setNavigationHistory] = useState<NavigationHistoryItem[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isNavigatingHistory = useRef(false);
  const document = selectedDocument;

  // Track navigation history when document changes
  useEffect(() => {
    if (!selectedDocument) {
      setActualFile(null);
      setNavigationHistory([]);
      setHistoryIndex(-1);
      isNavigatingHistory.current = false;
      return;
    }

    // Skip adding to history if we're navigating through history
    if (isNavigatingHistory.current) {
      isNavigatingHistory.current = false;
      
      // Restore page number if navigating back to a PDF
      const currentHistoryItem = navigationHistory[historyIndex];
      if (currentHistoryItem?.pageNumber && selectedDocument.file.name.toLowerCase().endsWith('.pdf')) {
        // Use setTimeout to ensure PDF viewer is ready
        setTimeout(() => {
          if (pdfViewerRef.current && pdfViewerRef.current.jumpToPage) {
            pdfViewerRef.current.jumpToPage(currentHistoryItem.pageNumber!); // Pass 1-based page number
            console.log('📄 Restored page number:', currentHistoryItem.pageNumber);
          }
        }, 500);
      }
      return;
    }

    // Add to navigation history if it's a new document
    setNavigationHistory(prev => {
      // Use functional update to get the latest historyIndex
      const currentIndex = historyIndex;
      const newHistory = prev.slice(0, currentIndex + 1);
      const newItem: NavigationHistoryItem = {
        document: selectedDocument,
        timestamp: Date.now(),
      };
      
      // Don't add duplicate if it's the same document as the last one
      // Compare by file name, sequence, AND title to avoid false positives
      if (newHistory.length > 0) {
        const lastDoc = newHistory[newHistory.length - 1].document;
        const isDuplicate = 
          lastDoc.file.name === selectedDocument.file.name &&
          lastDoc.seq === selectedDocument.seq &&
          lastDoc.title === selectedDocument.title &&
          lastDoc.href === selectedDocument.href &&
          (lastDoc.path || '') === (selectedDocument.path || '');
        
        if (isDuplicate) {
          // Silently skip duplicate document (same document selected again)
          return prev;
        }
      }
      
      newHistory.push(newItem);
      const newIndex = newHistory.length - 1;
      
      // Update historyIndex synchronously to ensure button states update correctly
      setHistoryIndex(newIndex);
      return newHistory;
    });
  }, [selectedDocument, historyIndex]);

  // Update back/forward button states - prioritize PDF page navigation
  useEffect(() => {
    // Use refs to track previous values and avoid unnecessary state updates
    const historyLength = navigationHistory.length;
    const docCanGoBack = historyLength > 0 && historyIndex > 0;
    const docCanGoForward = historyLength > 0 && historyIndex < historyLength - 1;
    
    // For PDFs, prioritize PDF viewer history (page-level navigation within PDF)
    // Only fall back to document history if PDF has no page history
    if (document && document.file.name.toLowerCase().endsWith('.pdf')) {
      const pdfCanGoBack = pdfViewerRef.current?.canGoBack || false;
      const pdfCanGoForward = pdfViewerRef.current?.canGoForward || false;
      
      const newCanGoBack = pdfCanGoBack || (!pdfCanGoBack && docCanGoBack);
      const newCanGoForward = pdfCanGoForward || (!pdfCanGoForward && docCanGoForward);
      
      setCanGoBack(prev => prev !== newCanGoBack ? newCanGoBack : prev);
      setCanGoForward(prev => prev !== newCanGoForward ? newCanGoForward : prev);
      
      // Poll PDF viewer state periodically for PDFs (less frequently to avoid excessive re-renders)
      const interval = setInterval(() => {
        const currentPdfCanGoBack = pdfViewerRef.current?.canGoBack || false;
        const currentPdfCanGoForward = pdfViewerRef.current?.canGoForward || false;
        
        const updatedCanGoBack = currentPdfCanGoBack || (!currentPdfCanGoBack && docCanGoBack);
        const updatedCanGoForward = currentPdfCanGoForward || (!currentPdfCanGoForward && docCanGoForward);
        
        setCanGoBack(prev => prev !== updatedCanGoBack ? updatedCanGoBack : prev);
        setCanGoForward(prev => prev !== updatedCanGoForward ? updatedCanGoForward : prev);
      }, 1000);
      
      return () => clearInterval(interval);
    } else {
      setCanGoBack(docCanGoBack);
      setCanGoForward(docCanGoForward);
    }
  }, [historyIndex, navigationHistory.length, document]);

  // Lazy load SharePoint file content when document is selected
  useEffect(() => {
    if (!selectedDocument) {
      setActualFile(null);
      return;
    }

    const loadFile = async () => {
      // Check if this is a SharePoint file
      if (isSharePointFile(selectedDocument.file)) {
        setLoadingContent(true);
        
        try {
          // Download file content on-demand
          const blob = await loadSharePointFileContent(selectedDocument.file);
          
          // Create a new File object with actual content
          const fileWithContent = new File([blob], selectedDocument.file.name, {
            type: selectedDocument.file.type,
            lastModified: selectedDocument.file.lastModified,
          });
          
          setActualFile(fileWithContent);
        } catch (error) {
          console.error('Failed to load file content:', error);
          setActualFile(null);
        } finally {
          setLoadingContent(false);
        }
      } else {
        // Local file - use directly
        setActualFile(selectedDocument.file);
      }
    };

    loadFile();
  }, [selectedDocument]);

  const handleDownload = useCallback(async () => {
    if (!document) return;
    
    const fileToDownload = actualFile || document.file;
    
    const url = URL.createObjectURL(fileToDownload);
    const anchor = window.document.createElement('a');
    anchor.href = url;
    anchor.download = document.file.name;
    window.document.body.appendChild(anchor);
    anchor.click();
    window.document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, [document, actualFile]);

  const handleGoBack = useCallback(() => {
    // For PDFs, prioritize PDF page navigation (within the same document)
    if (document && document.file.name.toLowerCase().endsWith('.pdf') && pdfViewerRef.current) {
      // Check if PDF viewer has page history to go back to
      if (pdfViewerRef.current.canGoBack) {
        pdfViewerRef.current.goBack();
        return;
      }
    }
    
    // If no PDF page history, then try document-level navigation
    if (historyIndex > 0 && onDocumentSelect && navigationHistory.length > 0) {
      const prevItem = navigationHistory[historyIndex - 1];
      isNavigatingHistory.current = true;
      setHistoryIndex(historyIndex - 1);
      onDocumentSelect(prevItem.document);
    } else {
      // If no previous document in history, close the viewer
      onClose();
    }
  }, [document, historyIndex, navigationHistory, onDocumentSelect, onClose]);

  const handleGoForward = useCallback(() => {
    // Always try iframe history first for same-document page navigation
    if (document && document.file.name.toLowerCase().endsWith('.pdf') && pdfViewerRef.current) {
      // Always attempt PDF navigation - browser will handle it if possible
      pdfViewerRef.current.goForward();
    } else if (historyIndex < navigationHistory.length - 1 && onDocumentSelect) {
      // For non-PDF or if no PDF viewer, use document-level navigation
      const nextItem = navigationHistory[historyIndex + 1];
      isNavigatingHistory.current = true;
      setHistoryIndex(historyIndex + 1);
      onDocumentSelect(nextItem.document);
    }
  }, [document, pdfViewerRef, historyIndex, navigationHistory, onDocumentSelect]);

  const handleExternalLink = useCallback((linkPath: string, pageNumber?: number, namedDestination?: string) => {
    if (!onDocumentSelect) return;
    
    console.log('🔍 Searching for document with path:', linkPath);
    console.log('📄 Current document:', document?.title);
    
    // Save current page number before navigating away (for PDFs)
    let currentPageNumber: number | undefined;
    if (document && document.file.name.toLowerCase().endsWith('.pdf') && pdfViewerRef.current) {
      // Get current page from PDF viewer
      currentPageNumber = pdfViewerRef.current.currentPage || 1;
      console.log('📄 Saving current page before navigation:', currentPageNumber);
    }

    // Same-directory exact resolution. Uses the source PDF's webkitRelativePath to
    // construct the target's exact path, bypassing fuzzy matching. Handles
    // flat-published sequences where bare filenames collide across 32s-drug-sub
    // and 32p-drug-prod sub-trees (alphabetical tree walk would otherwise pick
    // the P-side file first).
    let earlyMatch: TreeNode | null = null;
    if (document?.file && linkPath && !/^https?:\/\//i.test(linkPath)) {
      const sourceWebkitPath = (document.file as { webkitRelativePath?: string }).webkitRelativePath;
      if (sourceWebkitPath) {
        const lastSlash = sourceWebkitPath.lastIndexOf('/');
        if (lastSlash > 0) {
          const sourceDir = sourceWebkitPath.substring(0, lastSlash);
          const normalizedLink = linkPath.replace(/\\/g, '/');
          const joined = normalizedLink.startsWith('/')
            ? normalizedLink.slice(1)
            : `${sourceDir}/${normalizedLink}`;
          const collapsed: string[] = [];
          for (const seg of joined.split('/')) {
            if (seg === '..') collapsed.pop();
            else if (seg !== '' && seg !== '.') collapsed.push(seg);
          }
          const target = collapsed.join('/');
          const candidate = findNodeByWebkitPath(tree, target);
          if (candidate && candidate.type === 'file') {
            earlyMatch = candidate;
            console.log('✅ Resolved via source-directory match:', target);
          }
        }
      }
    }

    // Resolve relative paths (e.g., ../../../folder/file.pdf)
    let resolvedPath = linkPath;
    if (linkPath.startsWith('../') || linkPath.startsWith('./')) {
      // Get the current document's file system path to resolve relative path
      const currentDocSearchKey = document?.path || document?.href || document?.title || '';
      const currentDocNode = findDocumentByPath(
        allSequences, 
        tree, 
        currentDocSearchKey, 
        cumulativeTree, 
        getCumulativeTree
      );
      
      if (currentDocNode && currentDocNode.type === 'file') {
        const currentFilePath = getFileSystemPath(currentDocNode);
        if (currentFilePath) {
          // Resolve relative path: split current path, go up directories, then append relative path
          const currentPathParts = currentFilePath.split('/').filter(Boolean);
          const relativePathParts = linkPath.split('/').filter(Boolean);
          
          // Count how many levels up (../)
          let levelsUp = 0;
          let relativeStartIndex = 0;
          for (let i = 0; i < relativePathParts.length; i++) {
            if (relativePathParts[i] === '..') {
              levelsUp++;
              relativeStartIndex = i + 1;
            } else if (relativePathParts[i] !== '.') {
              break;
            }
          }
          
          // Build resolved path: go up N levels, then append remaining path
          if (levelsUp > 0 && currentPathParts.length >= levelsUp) {
            const basePath = currentPathParts.slice(0, currentPathParts.length - levelsUp);
            const remainingPath = relativePathParts.slice(relativeStartIndex);
            resolvedPath = [...basePath, ...remainingPath].join('/');
            console.log('🔧 Resolved relative path:', linkPath, '->', resolvedPath);
          }
        }
      }
    }
    
    // If link path is just a filename, try to find it in the same module/folder as current document
    const linkPathParts = resolvedPath.split('/');
    const isJustFilename = linkPathParts.length === 1;
    
    let foundNode: TreeNode | null = earlyMatch;

    if (isJustFilename && document && !foundNode) {
      // For filename-only links, try to find in the same context as current document
      // Get the current document's path to use as context
      const currentDocSearchKey = document.path || document.href || document.title;
      const currentDocNode = findDocumentByPath(
        allSequences, 
        tree, 
        currentDocSearchKey, 
        cumulativeTree, 
        getCumulativeTree
      );
      
      if (currentDocNode && currentDocNode.type === 'file') {
        const currentFilePath = getFileSystemPath(currentDocNode);
        if (currentFilePath) {
          // Extract module and folder path from current document
          const currentPathParts = currentFilePath.split('/');
          const moduleIndex = currentPathParts.findIndex(p => /^m[1-5]$/i.test(p));
          
          if (moduleIndex >= 0) {
            // Try to find the file in the same module/folder structure
            // Build potential paths: module/.../filename, module/subfolder/filename, etc.
            const module = currentPathParts[moduleIndex];
            const potentialPaths = [
              `${module}/${resolvedPath}`, // Same module, root
              ...currentPathParts.slice(moduleIndex + 1, -1).map((_, i, arr) => 
                `${module}/${arr.slice(0, i + 1).join('/')}/${resolvedPath}`
              ) // Same module, various subfolders
            ];
            
            console.log('🔍 Trying potential paths for filename-only link:', potentialPaths);
            
            for (const potentialPath of potentialPaths) {
              foundNode = findDocumentByPath(
                allSequences, 
                tree, 
                potentialPath, 
                cumulativeTree, 
                getCumulativeTree
              );
              if (foundNode) {
                console.log('✅ Found via potential path:', potentialPath);
                break;
              }
            }
          }
        }
      }
    }
    
    // If not found via context, try direct search with resolved path
    if (!foundNode) {
      foundNode = findDocumentByPath(allSequences, tree, resolvedPath, cumulativeTree, getCumulativeTree);
    }
    
    // If still not found and we have a resolved path with multiple segments,
    // try searching by matching the end of the path (last few segments)
    // This handles cases where folder structure might differ slightly
    if (!foundNode && resolvedPath !== linkPath && resolvedPath.includes('/')) {
      const resolvedParts = resolvedPath.split('/').filter(Boolean);
      // Try matching just the last 4-5 segments (filename + parent folders)
      for (let segments = Math.min(5, resolvedParts.length); segments >= 2; segments--) {
        const partialPath = resolvedParts.slice(-segments).join('/');
        console.log('🔍 Trying partial path match:', partialPath);
        foundNode = findDocumentByPath(allSequences, tree, partialPath, cumulativeTree, getCumulativeTree);
        if (foundNode) {
          console.log('✅ Found via partial path:', partialPath);
          break;
        }
      }
    }
    
    if (foundNode && foundNode.type === 'file') {
      console.log('✅ Found document:', foundNode.name, 'in sequence:', foundNode.sequenceId);
      
      // Update current document's history entry with page number before navigating
      // Do this synchronously before calling onDocumentSelect to ensure historyIndex is correct
      if (document && currentPageNumber !== undefined) {
        setNavigationHistory(prev => {
          const updated = [...prev];
          const currentIdx = historyIndex;
          if (updated.length > 0 && currentIdx >= 0 && currentIdx < updated.length) {
            updated[currentIdx] = {
              ...updated[currentIdx],
              pageNumber: currentPageNumber,
            };
            console.log('📄 Updated history entry at index', currentIdx, 'with page number:', currentPageNumber);
          }
          return updated;
        });
      }
      
      const foundPath = getFileSystemPath(foundNode) || foundNode.originalName || foundNode.name;
      const newDocument: DocumentPreview = {
        title: foundNode.xmlTitle || foundNode.name,
        file: foundNode.file,
        seq: foundNode.sequenceId,
        href: foundNode.originalName || foundNode.name,
        op: foundNode.lifecycleOp || 'new',
        path: foundPath ? foundPath.toLowerCase() : undefined,
        initialPage: pageNumber, // Pass page number to document
        initialNamedDestination: namedDestination, // Pass named destination to document
      };
      
      // Use setTimeout to ensure history update completes before adding new document
      setTimeout(() => {
        onDocumentSelect(newDocument);
      }, 0);
    } else {
      console.warn('❌ Document not found for path:', resolvedPath);
      console.warn('   Original path:', linkPath);
      // Log some sample paths from the tree for debugging
      if (tree && tree.length > 0) {
        const samplePaths: string[] = [];
        const collectSamplePaths = (nodes: TreeNode[], depth = 0) => {
          if (depth > 3 || samplePaths.length >= 5) return;
          for (const node of nodes.slice(0, 3)) {
            if (node.type === 'file') {
              const path = getFileSystemPath(node);
              if (path && path.includes('specification')) {
                samplePaths.push(path);
              }
            } else if (node.type === 'folder' && node.children) {
              collectSamplePaths(node.children, depth + 1);
            }
          }
        };
        collectSamplePaths(tree);
        if (samplePaths.length > 0) {
          console.log('📋 Sample paths containing "specification":', samplePaths);
        }
      }
    }
  }, [onDocumentSelect, allSequences, tree, cumulativeTree, getCumulativeTree, document]);

  // Open-comment count for the badge (current sequence)
  const seqOpenCount = useMemo(
    () =>
      annotations.filter((a) => !a.resolved && (!currentSequence || a.sequenceId === currentSequence)).length,
    [annotations, currentSequence]
  );

  // All comments grouped by sequence + document for the review panel
  const commentGroups = useMemo(() => {
    const groups = new Map<string, { sequenceId: string; docTitle: string; items: Annotation[] }>();
    annotations.forEach((a) => {
      const key = `${a.sequenceId}|${a.docKey}`;
      if (!groups.has(key)) {
        groups.set(key, { sequenceId: a.sequenceId, docTitle: a.docTitle, items: [] });
      }
      groups.get(key)!.items.push(a);
    });
    const list = Array.from(groups.values());
    list.forEach((g) => g.items.sort((a, b) => a.page - b.page || a.createdAt.getTime() - b.createdAt.getTime()));
    list.sort((a, b) => a.sequenceId.localeCompare(b.sequenceId) || a.docTitle.localeCompare(b.docTitle));
    return list;
  }, [annotations]);

  // Navigate to the document (and page) a comment refers to
  const handleCommentNavigate = useCallback(
    (a: Annotation) => {
      if (!onDocumentSelect) return;
      const foundNode = findDocumentByPath(allSequences, tree, a.docPath, cumulativeTree, getCumulativeTree);
      if (foundNode && foundNode.type === 'file') {
        const foundPath = getFileSystemPath(foundNode) || foundNode.originalName || foundNode.name;
        onDocumentSelect({
          title: foundNode.xmlTitle || foundNode.name,
          file: foundNode.file,
          seq: foundNode.sequenceId,
          href: foundNode.originalName || foundNode.name,
          op: foundNode.lifecycleOp || 'new',
          path: foundPath ? foundPath.toLowerCase() : undefined,
          initialPage: a.page,
        });
      } else {
        console.warn('Comment target document not found in loaded package:', a.docPath);
      }
    },
    [onDocumentSelect, allSequences, tree, cumulativeTree, getCumulativeTree]
  );

  // Shared-annotation context: same docKey for every user who opens this file
  const annotationContext = useMemo((): AnnotationContext | undefined => {
    if (!document || !user) return undefined;
    const rawPath = document.path || document.href || document.title;
    return {
      docKey: buildDocKey(appId, document.seq, rawPath),
      appId: appId || 'app',
      sequenceId: document.seq,
      docPath: rawPath,
      docTitle: document.title,
      user: { uid: user.uid, email: user.email, role: user.role },
    };
  }, [document, user, appId]);

  const documentContent = useMemo(() => {
    if (!document) return null;

    // If loading SharePoint content, show loading indicator
    if (loadingContent) {
      return (
        <div className="loading-content-container">
          <Cloud className="loading-icon" />
          <p>Loading from SharePoint...</p>
          <small>Downloading {document.title}</small>
        </div>
      );
    }

    // If no actual file loaded yet, show message
    if (!actualFile) {
      return (
        <div className="loading-content-container">
          <FileText className="empty-icon" />
          <p>Preparing document...</p>
        </div>
      );
    }

    const fileName = actualFile.name.toLowerCase();
    const fileExtension = fileName.split('.').pop();

    switch (fileExtension) {
      case 'pdf':
        return (
          <SimplePDFViewer
            ref={pdfViewerRef}
            key={actualFile.name + actualFile.size + actualFile.lastModified}
            file={actualFile}
            onExternalLink={handleExternalLink}
            initialPage={document.initialPage}
            initialNamedDestination={document.initialNamedDestination}
            annotation={annotationContext}
          />
        );
      case 'xml':
        return <XMLViewer key={actualFile.name + actualFile.size + actualFile.lastModified} file={actualFile} isFullyExpanded={isFullyExpanded} />;
      case 'html':
      case 'css':
      case 'js':
      case 'ts':
      case 'json':
      case 'txt':
      case 'log':
      case 'md5':
      case 'csv':
        return <TextFileViewer key={actualFile.name + actualFile.size + actualFile.lastModified} file={actualFile} />;
      default:
        return <UnsupportedFileViewer key={actualFile.name + actualFile.size + actualFile.lastModified} file={actualFile} />;
    }
  }, [document, actualFile, loadingContent, isFullyExpanded, onClose, handleGoBack, handleExternalLink, annotationContext]);

  return (
    <div className={`document-viewer${!document ? ' empty' : ''}`} ref={viewerRef}>
      <div className="document-header" aria-label="Preview header">
        {document ? (
          <div className="document-header-content">
            <div className="document-info">
              <h3 className="document-title">{document.title}</h3>
            </div>
            
            <div className="document-controls">
              <button
                className={`control-button comments-toggle ${showCommentsPanel ? 'active' : ''}`}
                onClick={() => setShowCommentsPanel((v) => !v)}
                title={`Review comments in this sequence (${seqOpenCount} open)`}
              >
                <MessageSquare className="icon" />
                {seqOpenCount > 0 && <span className="comments-toggle-badge">{seqOpenCount}</span>}
              </button>

              {document && (
                <>
                  <button
                    className="control-button"
                    onClick={handleGoBack}
                    disabled={!canGoBack}
                    title="Go back"
                  >
                    <ArrowLeft className="icon" />
                  </button>
                  
                  <button
                    className="control-button"
                    onClick={handleGoForward}
                    disabled={!canGoForward}
                    title="Go forward"
                  >
                    <ArrowRight className="icon" />
                  </button>
                </>
              )}
              
              {document && document.file.name.toLowerCase().endsWith('.xml') && (
                <>
                  <button
                    className="control-button"
                    onClick={() => setIsFullyExpanded(true)}
                    title="Expand all XML nodes"
                  >
                    <Plus className="icon" />
                  </button>
                  
                  <button
                    className="control-button"
                    onClick={() => setIsFullyExpanded(false)}
                    title="Collapse all XML nodes"
                  >
                    <Minus className="icon" />
                  </button>
                </>
              )}
              
              <button
                className="control-button"
                onClick={handleDownload}
                title="Download document"
              >
                <Download className="icon" />
              </button>
              
              <button
                className="control-button close-button"
                onClick={onClose}
                title="Close document"
              >
                <X className="icon" />
              </button>
            </div>
          </div>
        ) : (
          <div className="document-header-content">
            <div className="document-info">
              <h3 className="document-title">Preview</h3>
            </div>
            <div className="document-controls">
              <button
                className={`control-button comments-toggle ${showCommentsPanel ? 'active' : ''}`}
                onClick={() => setShowCommentsPanel((v) => !v)}
                title={`Review comments in this sequence (${seqOpenCount} open)`}
              >
                <MessageSquare className="icon" />
                {seqOpenCount > 0 && <span className="comments-toggle-badge">{seqOpenCount}</span>}
              </button>

              <button
                className="control-button"
                title="Download document"
                disabled
              >
                <Download className="icon" />
              </button>

              <button
                className="control-button close-button"
                title="Close document"
                disabled
              >
                <X className="icon" />
              </button>
            </div>
          </div>
        )}
      </div>
      
      <div className="document-content">
        {document ? (
          documentContent
        ) : (
          <div className="empty-state">
            <FileText className="empty-icon" />
            <h3>No Document Selected</h3>
            <p className="empty-hint">Select a document from the outline to view it here</p>
          </div>
        )}

        {showCommentsPanel && (
          <div className="seq-comments-panel">
            <div className="seq-comments-header">
              <span className="seq-comments-title">
                <MessageSquare className="icon" />
                Review Comments
              </span>
              <div className="seq-comments-actions">
                {onExportComments && annotations.length > 0 && (
                  <button
                    className="seq-comments-export"
                    onClick={onExportComments}
                    title={`Export ${annotations.length} review comment(s) to Excel`}
                  >
                    <FileSpreadsheet className="icon" />
                  </button>
                )}
                <button className="seq-comments-close" onClick={() => setShowCommentsPanel(false)} title="Hide comments">
                  <X className="icon" />
                </button>
              </div>
            </div>
            <div className="seq-comments-body">
              {commentGroups.length === 0 ? (
                <div className="seq-comments-empty">No comments in this application yet.</div>
              ) : (
                commentGroups.map((group) => (
                  <div key={`${group.sequenceId}|${group.docTitle}`} className="seq-comments-group">
                    <div className="seq-comments-doc">
                      <span className="seq-comments-seq">[{group.sequenceId}]</span> {group.docTitle}
                    </div>
                    <ul>
                      {group.items.map((a) => (
                        <li
                          key={a.id}
                          className={`seq-comment ${a.resolved ? 'resolved' : ''}`}
                          onClick={() => handleCommentNavigate(a)}
                          title="Open this document at the commented page"
                        >
                          <span className={`seq-comment-status ${a.resolved ? 'resolved' : 'open'}`} />
                          <div className="seq-comment-main">
                            <div className="seq-comment-text">{a.text}</div>
                            <div className="seq-comment-meta">
                              p.{a.page} · {a.authorEmail.split('@')[0]} · {a.createdAt.toLocaleDateString()}
                              {a.resolved ? ' · resolved' : ''}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// XML viewer component with proper formatting
function XMLViewer({ file, isFullyExpanded }: { file: File; isFullyExpanded: boolean }) {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        setError(null);
        const text = await file.text();
        setContent(text);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load file');
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [file]);

  // Update expansion state when isFullyExpanded changes
  useEffect(() => {
    if (isFullyExpanded && content) {
      try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(content, 'text/xml');
        const allNodeIds = getAllNodeIds(xmlDoc.documentElement, 'root');
        setExpandedNodes(new Set(allNodeIds));
      } catch (err) {
        // If parsing fails, just clear the expanded nodes
        setExpandedNodes(new Set());
      }
    } else {
      setExpandedNodes(new Set());
    }
  }, [isFullyExpanded, content]);

  const getAllNodeIds = (element: Element, parentId: string): string[] => {
    const nodeId = parentId;
    const children = Array.from(element.children);
    const childIds = children.flatMap((child, index) => 
      getAllNodeIds(child, `${parentId}-${index}`)
    );
    return [nodeId, ...childIds];
  };

  const toggleNode = (nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="xml-viewer loading">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="xml-viewer error">
        <div className="error-message">Error: {error}</div>
      </div>
    );
  }

  const renderXMLTree = (xml: string) => {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xml, 'text/xml');
      
      // Check for parsing errors
      const parseError = xmlDoc.querySelector('parsererror');
      if (parseError) {
        return <pre className="xml-content">{xml}</pre>;
      }

      return renderXMLElement(xmlDoc.documentElement, 0, 'root');
    } catch (err) {
      return <pre className="xml-content">{xml}</pre>;
    }
  };

  const renderXMLElement = (element: Element, depth: number, nodeId: string): JSX.Element => {
    const hasChildren = element.children.length > 0;
    const hasTextContent = element.textContent && element.textContent.trim() && !hasChildren;
    const isExpanded = expandedNodes.has(nodeId);
    
    return (
      <div key={nodeId} className="xml-tree-node">
        <div className="xml-node-line">
          {/* Tree lines and expand/collapse button */}
          <div className="xml-tree-controls" style={{ paddingLeft: `${depth * 16}px` }}>
            {hasChildren ? (
              <button 
                className="xml-expand-button" 
                title={isExpanded ? "Click to collapse" : "Click to expand"}
                onClick={() => toggleNode(nodeId)}
              >
                {isExpanded ? '−' : '+'}
              </button>
            ) : (
              <span className="xml-no-expand"></span>
            )}
          </div>
          
          {/* XML tag */}
          <div className="xml-tag-content">
            <span className="xml-tag-bracket">&lt;</span>
            <span className="xml-tag-name">{element.nodeName}</span>
            
            {/* Attributes */}
            {Array.from(element.attributes).map((attr) => (
              <span key={attr.name} className="xml-attribute">
                <span className="xml-attr-name"> {attr.name}</span>
                <span className="xml-attr-equals">=</span>
                <span className="xml-attr-value">"{attr.value}"</span>
              </span>
            ))}
            
            {!hasChildren && !hasTextContent ? (
              <span className="xml-tag-bracket"> /&gt;</span>
            ) : (
              <span className="xml-tag-bracket">&gt;</span>
            )}
          </div>
        </div>
        
        {/* Text content */}
        {hasTextContent && (
          <div className="xml-text-line">
            <div className="xml-tree-spacer" style={{ paddingLeft: `${(depth + 1) * 16}px` }}></div>
            <div className="xml-text-content">{element.textContent}</div>
          </div>
        )}
        
        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="xml-children">
            {Array.from(element.children).map((child, index) => {
              const childId = `${nodeId}-${index}`;
              return renderXMLElement(child as Element, depth + 1, childId);
            })}
          </div>
        )}
        
        {/* Closing tag */}
        {hasChildren && isExpanded && (
          <div className="xml-node-line">
            <div className="xml-tree-controls" style={{ paddingLeft: `${depth * 16}px` }}>
              <span className="xml-no-expand"></span>
            </div>
            <div className="xml-tag-content">
              <span className="xml-tag-bracket">&lt;/</span>
              <span className="xml-tag-name">{element.nodeName}</span>
              <span className="xml-tag-bracket">&gt;</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="xml-viewer">
      {renderXMLTree(content)}
    </div>
  );
}

// Text file viewer component
function TextFileViewer({ file }: { file: File }) {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        setError(null);
        const text = await file.text();
        setContent(text);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load file');
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [file]);

  if (loading) {
    return (
      <div className="text-viewer loading">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-viewer error">
        <div className="error-message">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="text-viewer">
      <pre className="text-content">{content}</pre>
    </div>
  );
}

// Unsupported file viewer component
function UnsupportedFileViewer({ file }: { file: File }) {
  const handleDownload = () => {
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="unsupported-viewer">
      <div className="unsupported-content">
        <div className="file-icon">📄</div>
        <h3>Unsupported File Type</h3>
        <p>This file type cannot be previewed in the browser.</p>
        <p className="file-info">
          <strong>File:</strong> {file.name}<br />
          <strong>Size:</strong> {(file.size / 1024).toFixed(1)} KB<br />
          <strong>Type:</strong> {file.type || 'Unknown'}
        </p>
        <button className="download-button" onClick={handleDownload}>
          <Download className="icon" />
          Download File
        </button>
      </div>
    </div>
  );
}
